<script lang="ts">
  import Navbar from "$lib/components/Navbar.svelte";
  import PersonaPicker from "$lib/components/PersonaPicker.svelte";
  import { auth } from "$lib/stores/auth.svelte";
  import { theme } from "$lib/stores/theme.svelte";
  import { meStore, type Persona } from "$lib/stores/me.svelte";
  import { api } from "$lib/api/client";
  import { toasts } from "$lib/stores/toasts.svelte";

  interface Settings {
    notify_due_time?: string;
    notify_engagement_time?: string;
    notify_engagement_enabled?: boolean;
    telegram_chat_id?: string | null;
    telegram_configured?: boolean;
    _token_input?: string;
  }

  let settings = $state<Settings | null>(null);
  let saving = $state(false);
  let webauthnList = $state<any[]>([]);
  let webauthnBusy = $state(false);
  const WEBAUTHN_ENABLED = false; // backend não expõe /auth/webauthn/credentials ainda

  async function loadSettings() {
    const ws = auth.workspaces[0]; if (!ws) return;
    settings = await api(`/workspaces/${ws.id}/settings`);
  }

  async function loadWebauthn() {
    if (!WEBAUTHN_ENABLED) { webauthnList = []; return; }
    try { webauthnList = await api("/auth/webauthn/credentials"); }
    catch { webauthnList = []; }
  }

  $effect(() => { loadSettings(); loadWebauthn(); if (!meStore.me && !meStore.loading) meStore.load(); });

  // ===== Persona =====
  let personaSel = $state<Persona>(meStore.me?.persona ?? "personal");
  let savingPersona = $state(false);
  $effect(() => { if (meStore.me) personaSel = meStore.me.persona; });
  async function savePersona() {
    if (!meStore.me || personaSel === meStore.me.persona) return;
    savingPersona = true;
    try {
      await meStore.setPersona(personaSel);
      toasts.success("Perfil atualizado");
    } catch (e: any) {
      toasts.error("Erro", e?.body?.detail || e?.message || "falha");
    } finally { savingPersona = false; }
  }

  // ===== Privacy PIN =====
  let pinNew = $state("");
  let pinNew2 = $state("");
  let pinCurrent = $state("");
  let pinBusy = $state(false);
  let pinError = $state<string | null>(null);

  async function savePin() {
    pinError = null;
    if (!/^\d{4,8}$/.test(pinNew)) { pinError = "PIN deve ter 4 a 8 dígitos."; return; }
    if (pinNew !== pinNew2) { pinError = "PINs não conferem."; return; }
    pinBusy = true;
    try {
      const body: any = { pin: pinNew };
      if (meStore.me?.has_privacy_pin) body.current_pin = pinCurrent;
      await meStore.setPin(pinNew, body.current_pin);
      pinNew = pinNew2 = pinCurrent = "";
      toasts.success("PIN salvo");
    } catch (e: any) {
      if (e?.status === 403) pinError = "PIN atual incorreto.";
      else pinError = e?.body?.detail || "Falha ao salvar PIN.";
    } finally { pinBusy = false; }
  }

  async function removePin() {
    pinError = null;
    if (!/^\d{4,8}$/.test(pinCurrent)) { pinError = "Informe seu PIN atual."; return; }
    pinBusy = true;
    try {
      await meStore.clearPin(pinCurrent);
      pinCurrent = "";
      toasts.success("PIN removido");
    } catch (e: any) {
      if (e?.status === 403) pinError = "PIN incorreto.";
      else pinError = e?.body?.detail || "Falha ao remover PIN.";
    } finally { pinBusy = false; }
  }

  async function save(e: Event) {
    e.preventDefault();
    if (!settings) return;
    saving = true;
    try {
      const ws = auth.workspaces[0]!;
      const body: any = {
        notify_due_time: settings.notify_due_time,
        notify_engagement_time: settings.notify_engagement_time,
        notify_engagement_enabled: settings.notify_engagement_enabled,
        telegram_chat_id: settings.telegram_chat_id || null,
      };
      if (settings._token_input) body.telegram_bot_token = settings._token_input;
      settings = await api(`/workspaces/${ws.id}/settings`, { method: "PATCH", body: JSON.stringify(body) });
      toasts.success("Configurações salvas");
    } catch (e: any) {
      toasts.error("Erro", e.message);
    } finally { saving = false; }
  }

  // ===== Telegram test =====
  let tgTesting = $state(false);
  let tgTestResult = $state<{ ok: boolean; error?: string } | null>(null);

  async function testTelegram() {
    tgTesting = true;
    tgTestResult = null;
    try {
      tgTestResult = await api("/telegram/test", { method: "POST" });
    } catch (e: any) {
      tgTestResult = { ok: false, error: e?.body?.detail || e?.message || "Falha" };
    } finally { tgTesting = false; }
  }

  // ===== Convite =====
  let inviteUrl = $state("");
  let inviteLoading = $state(false);
  let inviteCopied = $state(false);

  async function generateInvite() {
    inviteLoading = true;
    try {
      const res = await api<{ url: string }>("/invite/generate", { method: "POST" });
      inviteUrl = res.url;
    } catch (e: any) {
      toasts.error("Erro", e?.body?.detail || e?.message);
    } finally { inviteLoading = false; }
  }

  async function copyInvite() {
    await navigator.clipboard.writeText(inviteUrl);
    inviteCopied = true;
    setTimeout(() => (inviteCopied = false), 2000);
  }

  // ===== WebAuthn =====
  function b64urlToBuf(s: string) {
    s = s.replace(/-/g, "+").replace(/_/g, "/");
    while (s.length % 4) s += "=";
    return Uint8Array.from(atob(s), (c) => c.charCodeAt(0)).buffer;
  }
  function bufToB64url(b: ArrayBuffer) {
    const bytes = new Uint8Array(b);
    let s = "";
    bytes.forEach((b) => (s += String.fromCharCode(b)));
    return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  }

  async function registerKey() {
    if (!("credentials" in navigator)) { toasts.error("Navegador sem WebAuthn"); return; }
    webauthnBusy = true;
    try {
      const opts: any = await api("/auth/webauthn/register/begin", { method: "POST", body: "{}" });
      opts.publicKey.challenge = b64urlToBuf(opts.publicKey.challenge);
      opts.publicKey.user.id = b64urlToBuf(opts.publicKey.user.id);
      const cred = (await navigator.credentials.create({ publicKey: opts.publicKey })) as any;
      const att = {
        id: cred.id,
        rawId: bufToB64url(cred.rawId),
        type: cred.type,
        response: {
          attestationObject: bufToB64url(cred.response.attestationObject),
          clientDataJSON: bufToB64url(cred.response.clientDataJSON),
        },
      };
      await api("/auth/webauthn/register/finish", { method: "POST", body: JSON.stringify(att) });
      toasts.success("Chave de segurança cadastrada");
      await loadWebauthn();
    } catch (e: any) {
      toasts.error("Falha ao registrar chave", e.message);
    } finally { webauthnBusy = false; }
  }

  async function removeKey(id: string) {
    if (!confirm("Remover esta chave?")) return;
    try { await api(`/auth/webauthn/credentials/${id}`, { method: "DELETE" }); await loadWebauthn(); }
    catch (e: any) { toasts.error("Erro", e.message); }
  }
</script>

<Navbar />
<main class="max-w-3xl mx-auto px-4 py-6 space-y-6">
  <h1 class="text-2xl font-bold">Configurações</h1>

  <!-- Perfil de uso -->
  <section class="card space-y-3">
    <h2 class="font-semibold">Perfil de uso</h2>
    <p class="text-sm text-muted">Define o tom da interface e categorias sugeridas.</p>
    <PersonaPicker bind:value={personaSel} />
    <div class="flex justify-end">
      <button class="btn-primary" disabled={savingPersona || personaSel === meStore.me?.persona}
              onclick={savePersona}>
        {savingPersona ? "Salvando…" : "Salvar perfil"}
      </button>
    </div>
  </section>

  <!-- Privacidade / PIN -->
  <section id="privacy" class="card space-y-3">
    <h2 class="font-semibold">Camada privada (PIN)</h2>
    <p class="text-sm text-muted">
      Defina um PIN de 4 a 8 dígitos para desbloquear transações marcadas como privadas.
      A camada privada se trava automaticamente após 5 minutos sem atividade.
    </p>
    {#if meStore.me?.has_privacy_pin}
      <div class="text-sm text-emerald-600">✓ PIN configurado</div>
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label class="label" for="pin-cur">PIN atual</label>
          <input id="pin-cur" type="text" inputmode="numeric" pattern="[0-9]*" autocomplete="new-password" style="-webkit-text-security: disc;" maxlength="8"
                 class="input" bind:value={pinCurrent} />
        </div>
        <div>
          <label class="label" for="pin-new">Novo PIN</label>
          <input id="pin-new" type="text" inputmode="numeric" pattern="[0-9]*" autocomplete="new-password" style="-webkit-text-security: disc;" maxlength="8"
                 class="input" bind:value={pinNew} />
        </div>
        <div>
          <label class="label" for="pin-new2">Confirmar novo PIN</label>
          <input id="pin-new2" type="text" inputmode="numeric" pattern="[0-9]*" autocomplete="new-password" style="-webkit-text-security: disc;" maxlength="8"
                 class="input" bind:value={pinNew2} />
        </div>
      </div>
      {#if pinError}<p class="text-sm text-red-600">{pinError}</p>{/if}
      <div class="flex gap-2 justify-end">
        <button class="btn-ghost text-red-600" onclick={removePin} disabled={pinBusy}>Remover PIN</button>
        <button class="btn-primary" onclick={savePin} disabled={pinBusy || !pinNew}>
          {pinBusy ? "Salvando…" : "Trocar PIN"}
        </button>
      </div>
    {:else}
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label class="label" for="pin-set">Defina um PIN</label>
          <input id="pin-set" type="text" inputmode="numeric" pattern="[0-9]*" autocomplete="new-password" style="-webkit-text-security: disc;" maxlength="8"
                 class="input" bind:value={pinNew} />
        </div>
        <div>
          <label class="label" for="pin-set2">Confirme o PIN</label>
          <input id="pin-set2" type="text" inputmode="numeric" pattern="[0-9]*" autocomplete="new-password" style="-webkit-text-security: disc;" maxlength="8"
                 class="input" bind:value={pinNew2} />
        </div>
      </div>
      {#if pinError}<p class="text-sm text-red-600">{pinError}</p>{/if}
      <div class="flex justify-end">
        <button class="btn-primary" onclick={savePin} disabled={pinBusy || !pinNew}>
          {pinBusy ? "Salvando…" : "Definir PIN"}
        </button>
      </div>
    {/if}
  </section>

  <!-- Tema -->
  <section class="card space-y-3">
    <h2 class="font-semibold">Aparência</h2>
    <div class="flex items-center gap-2">
      <button class="{theme.mode === 'light' ? 'btn-primary' : 'btn-secondary'}" onclick={() => theme.set('light')}>
        ☀️ Claro
      </button>
      <button class="{theme.mode === 'dark' ? 'btn-primary' : 'btn-secondary'}" onclick={() => theme.set('dark')}>
        🌙 Escuro
      </button>
      <button class="{theme.mode === 'system' ? 'btn-primary' : 'btn-secondary'}" onclick={() => theme.set('system')}>
        💻 Sistema
      </button>
    </div>
  </section>

  <!-- Notificações + Telegram -->
  {#if settings}
    <form onsubmit={save} class="card space-y-3">
      <h2 class="font-semibold">Notificações</h2>
      <div class="grid grid-cols-2 gap-3">
        <div>
          <label class="label" for="ndt">Horário de vencimentos</label>
          <input id="ndt" type="time" class="input" bind:value={settings.notify_due_time} />
        </div>
        <div>
          <label class="label" for="net">Horário de engajamento</label>
          <input id="net" type="time" class="input" bind:value={settings.notify_engagement_time} />
        </div>
      </div>
      <label class="flex items-center gap-2">
        <input type="checkbox" bind:checked={settings.notify_engagement_enabled} />
        <span>Lembrar de registrar gastos do dia</span>
      </label>

      <h2 class="font-semibold pt-3 divider">🤖 Telegram</h2>

      {#if settings.telegram_configured}
        <div class="flex items-center gap-2 text-sm text-emerald-600 bg-emerald-50 rounded-lg px-3 py-2">
          ✅ Bot configurado
          <button class="ml-auto text-xs underline hover:no-underline" onclick={testTelegram} disabled={tgTesting}>
            {tgTesting ? "Testando…" : "Testar conexão"}
          </button>
        </div>
        {#if tgTestResult}
          <div class="text-sm rounded p-2 {tgTestResult.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}">
            {tgTestResult.ok ? "✅ Mensagem enviada com sucesso no Telegram!" : `❌ ${tgTestResult.error}`}
          </div>
        {/if}
      {:else}
        <div class="rounded-lg p-3 text-sm space-y-1" style="background: var(--surface-alt)">
          <p class="font-medium">Como configurar:</p>
          <ol class="list-decimal list-inside space-y-1 text-muted">
            <li>Abra o <a href="https://t.me/BotFather" target="_blank" class="text-brand underline">@BotFather</a> no Telegram</li>
            <li>Envie <code class="bg-[var(--border)] px-1 rounded">/newbot</code> e siga as instruções</li>
            <li>Cole o token gerado no campo abaixo</li>
            <li>Abra seu novo bot, envie qualquer mensagem e cole seu Chat ID</li>
          </ol>
        </div>
      {/if}

      <div>
        <label class="label" for="tt">Bot Token {settings.telegram_configured ? "(deixe em branco para manter)" : ""}</label>
        <input id="tt" class="input font-mono text-xs" placeholder="123456789:AAFabc…" bind:value={settings._token_input} />
      </div>
      <div>
        <label class="label" for="tcid">Seu Chat ID</label>
        <div class="flex gap-2">
          <input id="tcid" class="input flex-1" bind:value={settings.telegram_chat_id} placeholder="123456789" inputmode="numeric" />
        </div>
        <p class="text-xs text-muted mt-1">
          Para descobrir seu Chat ID, acesse <a href="https://t.me/userinfobot" target="_blank" class="text-brand underline">@userinfobot</a> no Telegram.
        </p>
      </div>

      <button class="btn-primary" disabled={saving}>{saving ? "Salvando…" : "Salvar"}</button>
    </form>
  {/if}

  <!-- Convite -->
  <section class="card space-y-3">
    <div>
      <h2 class="font-semibold">🎁 Convidar amigos</h2>
      <p class="text-xs text-muted mt-1">Quem entrar pelo seu link ganha +15 dias de trial. Você também!</p>
    </div>
    {#if inviteUrl}
      <div class="flex gap-2">
        <input class="input flex-1 text-xs font-mono" readonly value={inviteUrl} />
        <button class="btn-primary flex-none" onclick={copyInvite}>
          {inviteCopied ? "✓ Copiado!" : "Copiar"}
        </button>
      </div>
      <p class="text-xs text-muted">Compartilhe este link com quem quiser convidar.</p>
    {:else}
      <button class="btn-primary" onclick={generateInvite} disabled={inviteLoading}>
        {inviteLoading ? "Gerando…" : "Gerar link de convite"}
      </button>
    {/if}
  </section>

  <!-- 2FA WebAuthn -->
  {#if WEBAUTHN_ENABLED}
  <section class="card space-y-3">
    <div class="flex items-start justify-between">
      <div>
        <h2 class="font-semibold">Verificação em duas etapas (2FA)</h2>
        <p class="text-xs text-muted mt-1">
          Cadastre uma chave de segurança ou biometria do dispositivo (Touch ID, Face ID, Windows Hello).
        </p>
      </div>
      <button class="btn-primary" onclick={registerKey} disabled={webauthnBusy}>
        {webauthnBusy ? "Aguarde…" : "+ Adicionar chave"}
      </button>
    </div>

    {#if webauthnList.length === 0}
      <p class="text-sm text-muted">Nenhuma chave cadastrada.</p>
    {:else}
      <ul class="divide-y" style="border-color: var(--border)">
        {#each webauthnList as k}
          <li class="py-2 flex items-center justify-between">
            <div>
              <div class="font-medium text-sm">🔐 {k.label || k.id.slice(0, 8)}</div>
              <div class="text-xs text-muted">Adicionada em {new Date(k.created_at).toLocaleDateString("pt-BR")}</div>
            </div>
            <button class="btn-ghost text-red-600 text-sm" onclick={() => removeKey(k.id)}>Remover</button>
          </li>
        {/each}
      </ul>
    {/if}
  </section>
  {/if}
</main>
